import { useMemo } from 'react';
import { useSimWorld } from '../../sim/useSimWorld/index';
import type { SimWorld } from '../../sim/useSimWorld/types';
import type { MatchInfo } from '../types';

export function useMpSimWorld(matchInfo: MatchInfo, localPlayerIndex: number): SimWorld {
  const seed = useMemo(() => {
    // Convert hex seed to number for the sim
    return Number.parseInt(matchInfo.seed, 16);
  }, [matchInfo.seed]);

  const sim = useSimWorld({
    matchSeed: Number.isNaN(seed) ? 42 : seed,
    localPlayerIndex,
    externalTick: true,
    ...(matchInfo.teams ? { teams: matchInfo.teams } : {}),
    // An authored map: the document, ruleset and seats the match-server
    // resolved and verified, so this seat builds the same board as every
    // other seat and as the server's own authoritative sim.
    ...(matchInfo.map
      ? {
          mapJson: matchInfo.map.document,
          ruleset: matchInfo.map.ruleset,
          seats: matchInfo.map.seats,
        }
      : {
          ...(matchInfo.ruleset ? { ruleset: matchInfo.ruleset } : {}),
          ...(matchInfo.seats ? { seats: matchInfo.seats } : {}),
        }),
  });

  // In multiplayer, tick is driven by the match-server, not the local tick loop.
  // For the first slice, the sim runs locally with the same seed — both clients
  // get identical state. The match-server verifies.
  // Full lockstep (intent relay instead of direct sim) is the next iteration.

  return sim;
}
